import { createReducer } from '@reduxjs/toolkit';
import type { Reducer } from '@reduxjs/toolkit';
import { PageType } from '@vgtv/api-client/lib/page';
import type { ComponentId } from '@vgtv/api-client/lib/component';
import type { SvpAssetId } from '@vgtv/api-client/lib/svp_asset';
import type { SvpCategoryId } from '@vgtv/api-client/lib/svp_category';
import type { CnpTagId } from '@vgtv/api-client/lib/cnp_tag';
import type { CnpStoryId } from '@vgtv/api-client/lib/cnp_story';

import {
  fetchPageByCategoryId,
  fetchPageBySlug,
  fetchPageByStoryId,
  fetchPageByTagId,
} from '../pages';
import { fetchSvpAssets } from '../assets';
import { fetchSvpCategories } from '../categories';
import { fetchProfiles } from '../profiles';
import { fetchSvpAssetById } from '../assets/actions';
import { fetchSeriesWatchtimes, fetchWatchtimes } from '../watchtime/actions';
import type { Vendor } from '../../types';

export * from './selectors';

export type FetchingState = {
  pageConfiguration: Record<
    Vendor,
    {
      byCategoryId: Partial<Record<SvpCategoryId, boolean>>;
      byTagId: Partial<Record<CnpTagId, boolean>>;
      byStoryId: Partial<Record<CnpStoryId, boolean>>;
      bySlug: Partial<Record<string, boolean>>;
    }
  >;
  componentConfiguration: Partial<Record<ComponentId, boolean>>;
  assets: Record<Vendor, Partial<Record<SvpAssetId, boolean>>>;
  watchtimes: {
    series: Record<Vendor, Partial<Record<SvpCategoryId, boolean>>>;
    assets: Record<Vendor, Partial<Record<SvpAssetId, boolean>>>;
  };
};

const initialState: FetchingState = {
  pageConfiguration: {
    vgtv: {
      byCategoryId: {},
      byTagId: {},
      byStoryId: {},
      bySlug: {},
    },
    ab: {
      byCategoryId: {},
      byTagId: {},
      byStoryId: {},
      bySlug: {},
    },
    brandstudio: {
      byCategoryId: {},
      byTagId: {},
      byStoryId: {},
      bySlug: {},
    },
  },
  componentConfiguration: {},
  assets: { vgtv: {}, ab: {}, brandstudio: {} },
  watchtimes: {
    assets: { vgtv: {}, ab: {}, brandstudio: {} },
    series: { vgtv: {}, ab: {}, brandstudio: {} },
  },
};

function setPageConfigurationFetching(pageType: PageType, value: boolean) {
  return (
    state: FetchingState,
    action: Pick<
      ReturnType<
        | typeof fetchPageBySlug.fulfilled
        | typeof fetchPageBySlug.pending
        | typeof fetchPageBySlug.rejected
        | typeof fetchPageByTagId.fulfilled
        | typeof fetchPageByTagId.pending
        | typeof fetchPageByTagId.rejected
        | typeof fetchPageByCategoryId.fulfilled
        | typeof fetchPageByCategoryId.pending
        | typeof fetchPageByCategoryId.rejected
        | typeof fetchPageByStoryId.fulfilled
        | typeof fetchPageByStoryId.pending
        | typeof fetchPageByStoryId.rejected
      >,
      'type' | 'payload' | 'meta'
    >
  ) => {
    const args = action.meta.arg;

    const { vendor } = args;

    if (pageType === PageType.CATEGORY && 'categoryId' in args) {
      state.pageConfiguration[vendor].byCategoryId[args.categoryId] = value;
    } else if (pageType === PageType.TAG && 'tagId' in args) {
      state.pageConfiguration[vendor].byTagId[args.tagId] = value;
    } else if (pageType === PageType.STORY && 'storyId' in args) {
      state.pageConfiguration[vendor].byStoryId[args.storyId] = value;
    } else if (pageType === PageType.CUSTOM && 'slug' in args) {
      state.pageConfiguration[vendor].bySlug[args.slug] = value;
    }
  };
}

function setComponentConfigurationFetching(value: boolean) {
  return (
    state: FetchingState,
    action: Pick<
      ReturnType<
        | typeof fetchSvpAssets.fulfilled
        | typeof fetchSvpCategories.fulfilled
        | typeof fetchProfiles.fulfilled
        | typeof fetchSvpAssets.pending
        | typeof fetchSvpAssets.rejected
      >,
      'meta'
    >
  ) => {
    const arg = action.meta.arg as { componentId?: ComponentId };
    if (arg.componentId) {
      state.componentConfiguration[arg.componentId] = value;
    }
  };
}

function setAssetFetching(value: boolean) {
  return (
    state: FetchingState,
    action: Pick<
      ReturnType<
        | typeof fetchSvpAssetById.fulfilled
        | typeof fetchSvpAssetById.pending
        | typeof fetchSvpAssetById.rejected
      >,
      'meta'
    >
  ) => {
    const { assetId, vendor } = action.meta.arg;

    state.assets[vendor][assetId] = value;
  };
}

function setAssetWatchtimeFetching(value: boolean) {
  return (
    state: FetchingState,
    action: Pick<
      ReturnType<
        | typeof fetchWatchtimes.fulfilled
        | typeof fetchWatchtimes.pending
        | typeof fetchWatchtimes.rejected
      >,
      'meta'
    >
  ) => {
    const { assetIds, vendor } = action.meta.arg;

    assetIds.forEach((assetId) => {
      state.watchtimes.assets[vendor][assetId] = value;
    });
  };
}

function setSeriesWatchtimeFetching(value: boolean) {
  return (
    state: FetchingState,
    action: Pick<
      ReturnType<
        | typeof fetchSeriesWatchtimes.pending
        | typeof fetchSeriesWatchtimes.rejected
      >,
      'meta'
    >
  ) => {
    const { vendor, seriesIds } = action.meta.arg;

    seriesIds.forEach((seriesId) => {
      state.watchtimes.series[vendor][seriesId] = value;
    });
  };
}

const fetchingReducer: Reducer<typeof initialState> = createReducer(
  initialState,
  (builder) =>
    builder
      .addCase(
        fetchPageByCategoryId.pending,
        setPageConfigurationFetching(PageType.CATEGORY, true)
      )
      .addCase(
        fetchPageByCategoryId.fulfilled,
        setPageConfigurationFetching(PageType.CATEGORY, false)
      )
      .addCase(
        fetchPageByCategoryId.rejected,
        setPageConfigurationFetching(PageType.CATEGORY, false)
      )
      .addCase(
        fetchPageByTagId.pending,
        setPageConfigurationFetching(PageType.TAG, true)
      )
      .addCase(
        fetchPageByTagId.fulfilled,
        setPageConfigurationFetching(PageType.TAG, false)
      )
      .addCase(
        fetchPageByTagId.rejected,
        setPageConfigurationFetching(PageType.TAG, false)
      )
      .addCase(
        fetchPageByStoryId.pending,
        setPageConfigurationFetching(PageType.STORY, true)
      )
      .addCase(
        fetchPageByStoryId.fulfilled,
        setPageConfigurationFetching(PageType.STORY, false)
      )
      .addCase(
        fetchPageByStoryId.rejected,
        setPageConfigurationFetching(PageType.STORY, false)
      )
      .addCase(
        fetchPageBySlug.pending,
        setPageConfigurationFetching(PageType.CUSTOM, true)
      )
      .addCase(
        fetchPageBySlug.fulfilled,
        setPageConfigurationFetching(PageType.CUSTOM, false)
      )
      .addCase(
        fetchPageBySlug.rejected,
        setPageConfigurationFetching(PageType.CUSTOM, false)
      )
      .addCase(fetchSvpAssets.pending, setComponentConfigurationFetching(true))
      .addCase(
        fetchSvpAssets.fulfilled,
        setComponentConfigurationFetching(false)
      )
      .addCase(
        fetchSvpAssets.rejected,
        setComponentConfigurationFetching(false)
      )
      .addCase(
        fetchSvpCategories.pending,
        setComponentConfigurationFetching(true)
      )
      .addCase(
        fetchSvpCategories.fulfilled,
        setComponentConfigurationFetching(false)
      )
      .addCase(
        fetchSvpCategories.rejected,
        setComponentConfigurationFetching(false)
      )
      .addCase(fetchProfiles.pending, setComponentConfigurationFetching(true))
      .addCase(
        fetchProfiles.fulfilled,
        setComponentConfigurationFetching(false)
      )
      .addCase(fetchProfiles.rejected, setComponentConfigurationFetching(false))
      .addCase(fetchSvpAssetById.pending, setAssetFetching(true))
      .addCase(fetchSvpAssetById.fulfilled, setAssetFetching(false))
      .addCase(fetchSvpAssetById.rejected, setAssetFetching(false))
      .addCase(fetchWatchtimes.pending, setAssetWatchtimeFetching(true))
      .addCase(fetchWatchtimes.fulfilled, setAssetWatchtimeFetching(false))
      .addCase(fetchWatchtimes.rejected, setAssetWatchtimeFetching(false))
      .addCase(fetchSeriesWatchtimes.pending, setSeriesWatchtimeFetching(true))
      .addCase(
        fetchSeriesWatchtimes.rejected,
        setSeriesWatchtimeFetching(false)
      )
      .addCase(fetchSeriesWatchtimes.fulfilled, (state, action) => {
        const { seriesIds, vendor } = action.meta.arg;

        seriesIds.forEach((seriesId) => {
          state.watchtimes.series[vendor][seriesId] = false;
        });

        action.payload.forEach((item) => {
          if (item.watchtime) {
            state.watchtimes.assets[vendor][item.watchtime.assetId] = false;
          }
        });
      })
);

export { fetchingReducer };
